(function () {
  "use strict";

  document.querySelectorAll("[data-video-reels]").forEach(initReelsWidget);

  const rootOverlays = new WeakMap();
  // placement handle -> { store, placementId } for the analytics calls
  // below, which only get passed the handle.
  const placementMeta = new Map();

  // ---- Playback-session tracking (shared with litelok-gallery.js — keep
  // the two copies identical) ----
  //
  // One session per play of one video in this page view, reported to
  // /api/analytics/playback (see the VideoView model in the admin app).
  // Records every second of the video actually played — a second counts
  // once playback crosses its midpoint, so seeking past it doesn't — plus
  // exactly where and why playback stopped, pauses, seeks, loops, sound
  // and fullscreen. Each beat carries the whole session so far with an
  // increasing `seq`; the server keeps the newest, so a lost or reordered
  // beacon never loses or double-counts anything. Beats go every few
  // seconds while playing and immediately on every stop.
  //
  // Replaces the old "watch_time"/"watch_complete" events, which sent a
  // running tally in 5s chunks: every chunk was counted as a separate
  // "view" and the last <5s of every watch was never sent.
  const PAGE_VIEW_ID = randomId();
  const liveTrackers = new Set();
  const trackerByVideo = new WeakMap();

  function randomId() {
    const bytes = new Uint8Array(12);
    if (window.crypto && window.crypto.getRandomValues) window.crypto.getRandomValues(bytes);
    else for (let i = 0; i < bytes.length; i++) bytes[i] = Math.floor(Math.random() * 256);
    return Array.from(bytes, (b) => b.toString(16).padStart(2, "0")).join("");
  }

  function deviceType() {
    if (!window.matchMedia("(pointer: coarse)").matches) return "desktop";
    return Math.min(window.screen.width, window.screen.height) < 600 ? "mobile" : "tablet";
  }

  function postBeacon(url, payload) {
    const body = JSON.stringify(payload);
    if (navigator.sendBeacon && navigator.sendBeacon(url, new Blob([body], { type: "application/json" }))) return;
    fetch(url, { method: "POST", headers: { "Content-Type": "application/json" }, body, keepalive: true }).catch(() => {});
  }

  // Why the next pause is happening, when our own code is the one pausing
  // (scrolled away, switched video, closed) — otherwise it's the viewer.
  function notePauseReason(video, reason) {
    const tracker = trackerByVideo.get(video);
    if (tracker && !video.paused) tracker.pauseReason = reason;
  }

  function trackPlayback(video, opts) {
    const HEARTBEAT_MS = 5000;
    const previous = trackerByVideo.get(video);
    if (previous) previous.end();

    const viewId = randomId();
    const device = deviceType();
    const plays = []; // plays[n] = times second n of the video has been played
    let seq = 0;
    let lastTime = video.currentTime || 0;
    let maxPosition = lastTime;
    let playing = false;
    let started = false;
    let pauses = 0;
    let seeksBack = 0;
    let seeksForward = 0;
    let lastSeekAt = 0;
    let loops = 0;
    let completed = false;
    let soundOn = false;
    let fullscreen = false;
    let exitReason = null;
    let timer = null;
    const tracker = { pauseReason: null, flush, end };

    function countPlayed(from, to) {
      for (let i = Math.floor(from - 0.5) + 1; i + 0.5 <= to; i++) {
        if (i < 0) continue;
        while (plays.length <= i) plays.push(0);
        plays[i]++;
      }
    }

    function advance() {
      const t = video.currentTime;
      const d = t - lastTime;
      if (playing && d > 0 && d < 4) countPlayed(lastTime, t);
      lastTime = t;
      if (t > maxPosition) maxPosition = t;
      if (!video.muted && video.volume > 0 && playing) soundOn = true;
    }

    function runs() {
      const out = [];
      for (let i = 0; i < plays.length; i++) {
        if (!plays[i]) continue;
        const last = out[out.length - 1];
        if (last && last[0] + last[1] === i && last[2] === plays[i]) last[1]++;
        else out.push([i, 1, plays[i]]);
      }
      return out;
    }

    function flush() {
      if (!started) return;
      const videoId = opts.getVideoId();
      // Not known yet (the gallery's id sync hasn't come back) — keep
      // accumulating; the next beat carries everything so far anyway.
      if (!videoId || !opts.apiBase) return;
      seq++;
      postBeacon(`${opts.apiBase}/api/analytics/playback`, {
        viewId,
        pageViewId: PAGE_VIEW_ID,
        videoId,
        placementId: opts.getPlacementId() || undefined,
        surface: opts.surface,
        store: opts.store,
        device,
        seq,
        duration: isFinite(video.duration) ? +video.duration.toFixed(2) : 0,
        position: +lastTime.toFixed(2),
        maxPosition: +maxPosition.toFixed(2),
        runs: runs(),
        pauses,
        seeksBack,
        seeksForward,
        loops,
        completed,
        soundOn,
        fullscreen,
        exitReason: exitReason || undefined,
      });
    }

    function stopHeartbeat() {
      clearInterval(timer);
      timer = null;
    }

    const on = {
      play() {
        started = true;
        playing = true;
        exitReason = null;
        tracker.pauseReason = null;
        lastTime = video.currentTime;
        if (!timer) timer = setInterval(flush, HEARTBEAT_MS);
      },
      timeupdate: advance,
      seeking() {
        const to = video.currentTime;
        // A looping video (Reels) jumps back to the start by itself — that's
        // a completed play-through, not the viewer seeking.
        if (video.loop && isFinite(video.duration) && lastTime >= video.duration - 1.5 && to < 1) {
          if (playing) countPlayed(lastTime, video.duration);
          loops++;
          completed = true;
        } else if (Math.abs(to - lastTime) >= 1) {
          // One drag along a seek bar fires many seeks — count it once.
          const now = Date.now();
          if (now - lastSeekAt > 700) {
            if (to < lastTime) seeksBack++;
            else seeksForward++;
          }
          lastSeekAt = now;
        }
        lastTime = to;
      },
      pause() {
        if (video.ended) return; // "ended" reports this one
        advance();
        playing = false;
        stopHeartbeat();
        exitReason = tracker.pauseReason || (document.visibilityState === "hidden" ? "left_page" : "paused");
        if (exitReason === "paused") pauses++;
        tracker.pauseReason = null;
        flush();
      },
      ended() {
        advance();
        if (isFinite(video.duration)) countPlayed(lastTime, video.duration);
        playing = false;
        stopHeartbeat();
        completed = true;
        exitReason = "ended";
        flush();
      },
      volumechange() {
        if (!video.muted && video.volume > 0 && playing) soundOn = true;
      },
      webkitbeginfullscreen() {
        fullscreen = true;
      },
    };
    function onFullscreenChange() {
      const el = document.fullscreenElement || document.webkitFullscreenElement;
      if (el && (el === video || el.contains(video))) fullscreen = true;
    }

    // Page going away (tab closed, navigated, app switched): report where
    // things stand. If it was still playing, that's where they left.
    tracker.leavePage = () => {
      if (playing) {
        advance();
        exitReason = "left_page";
      }
      flush();
    };

    function end() {
      stopHeartbeat();
      Object.keys(on).forEach((type) => video.removeEventListener(type, on[type]));
      document.removeEventListener("fullscreenchange", onFullscreenChange);
      liveTrackers.delete(tracker);
      if (trackerByVideo.get(video) === tracker) trackerByVideo.delete(video);
    }

    Object.keys(on).forEach((type) => video.addEventListener(type, on[type]));
    document.addEventListener("fullscreenchange", onFullscreenChange);
    liveTrackers.add(tracker);
    trackerByVideo.set(video, tracker);
    if (!video.paused) on.play();
    return tracker;
  }

  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "hidden") liveTrackers.forEach((t) => t.flush());
  });
  window.addEventListener("pagehide", () => liveTrackers.forEach((t) => t.leavePage()));

  // Which storefront this is: an explicit data-store on the widget wins;
  // otherwise it's read off the shop's own myshopify domain (litelok-us,
  // litelok-eu, ...), defaulting to UK. Store attribution used to be
  // passed in here and was lost in a later rewrite, so every Reels event
  // since was recorded with no store at all.
  function detectStore(root) {
    const explicit = (root.dataset.store || "").toLowerCase();
    if (explicit === "uk" || explicit === "us" || explicit === "eu") return explicit;
    const shop = ((window.Shopify && window.Shopify.shop) || "").toLowerCase();
    if (/(^|[-.])(us|usa)([-.]|$)/.test(shop)) return "us";
    if (/(^|[-.])(eu|europe)([-.]|$)/.test(shop)) return "eu";
    return "uk";
  }

  // Shopify serves the original, full-resolution file at a bare CDN URL —
  // fine for the file itself, wasteful for a small preview. Shopify's CDN
  // accepts a `width` query param to serve a properly downsized variant
  // instead, cutting transfer size (and therefore paint time). Only
  // applied to actual Shopify CDN URLs so an unrelated external image URL
  // isn't mangled.
  function optimizedThumbnail(url, width) {
    if (!url) return url;
    try {
      const u = new URL(url, window.location.href);
      if (!/(^|\.)cdn\.shopify\.com$/.test(u.hostname)) return url;
      u.searchParams.set("width", String(width));
      return u.toString();
    } catch {
      return url;
    }
  }

  // Three presentation styles, one shared data/analytics/full-screen-player
  // core underneath. Style and appearance (accent color, circle/card size,
  // floating position) are managed in the Litelok Reels dashboard, on the
  // journey itself, not as Shopify theme block settings — that's what lets
  // the dashboard show a live preview using the exact same widget code,
  // and keeps the Shopify block down to just "which journey" (data-placement
  // is the only thing the theme block needs to know). The data-style/
  // data-accent/etc attributes below are a fallback only, for the rare case
  // the API response is missing the style object.
  async function initReelsWidget(root) {
    const apiBase = root.dataset.apiBase;
    const placement = root.dataset.placement;
    if (!apiBase || !placement) return;
    const store = detectStore(root);

    let videos = [];
    let style = null;
    try {
      // Each store has its own journey under the same handle (even when
      // the US/EU ones reuse the UK videos' URLs), so ask for this store's.
      // A store with no journey under this handle at all gets the UK one
      // rather than an empty row.
      let data = await fetchPlacement(apiBase, placement, store);
      if (store !== "uk" && !data.placementId && !(data.videos || []).length) data = await fetchPlacement(apiBase, placement, "uk");
      videos = data.videos || [];
      style = data.style || null;
      // data-analytics="off" (the dashboard's own live preview) records
      // nothing, so previewing a journey never shows up as real traffic.
      placementMeta.set(placement, { store, placementId: data.placementId || null, analytics: root.dataset.analytics !== "off" });
    } catch (e) {
      // Fail silently — a missing reels widget shouldn't break the PDP.
      console.warn("Video reels: failed to load", e);
      return;
    }
    if (!videos.length) return;

    // Preview-only escape hatch: the dashboard's live preview page sets
    // this to show an in-progress, not-yet-saved style choice without
    // writing it to the database. Never set on the real storefront block —
    // there, style always comes from the fetched API response above.
    let previewOverride = null;
    try {
      previewOverride = JSON.parse(root.dataset.previewStyle || "null");
    } catch (e) {
      previewOverride = null;
    }

    const styleType = previewOverride?.style || style?.type || root.dataset.style || "stories";
    const resolvedCircleSize = previewOverride?.circleSize || style?.circleSize || Number(root.dataset.circleSize) || 100;
    root.style.setProperty("--lr-accent", previewOverride?.accentColor || style?.accentColor || root.dataset.accent || "#cea761");
    root.style.setProperty("--lr-circle-size", resolvedCircleSize + "px");
    root.style.setProperty(
      "--lr-card-width",
      (previewOverride?.cardWidth || style?.cardWidth || root.dataset.cardWidth || "140") + "px"
    );
    root.dataset.resolvedFloatingPosition =
      previewOverride?.floatingPosition || style?.floatingPosition || root.dataset.floatingPosition || "bottom-right";

    if (styleType === "carousel") {
      initCarousel(root, videos, apiBase, placement);
    } else if (styleType === "floating") {
      // No inline track for this style — it renders as a fixed-position
      // bubble appended to <body>, not into the widget's own spot in the
      // page flow, so collapse the (now-empty) wrapper instead of leaving
      // a stray margin gap where the track would have been.
      root.style.margin = "0";
      initFloating(root, videos, apiBase, placement);
    } else if (styleType === "hero") {
      initHero(root, videos, apiBase, placement);
    } else if (styleType === "grid") {
      initGrid(root, videos, apiBase, placement);
    } else if (styleType === "filmstrip") {
      initFilmstrip(root, videos, apiBase, placement, resolvedCircleSize);
    } else if (styleType === "spotlight") {
      initSpotlight(root, videos, apiBase, placement);
    } else {
      initStories(root, videos, apiBase, placement, resolvedCircleSize);
    }
  }

  // no-store: a browser's own disk cache can otherwise keep serving
  // whatever video list it first saw for this placement — for as long as
  // an hour — even after the dashboard adds/removes videos. The CDN edge in
  // front of this API still caches server-side (Cache-Control: s-maxage),
  // so this only removes the unreliable client-side cache layer, not the
  // fast edge one.
  async function fetchPlacement(apiBase, placement, store) {
    const res = await fetch(`${apiBase}/api/public/${encodeURIComponent(placement)}?store=${encodeURIComponent(store)}`, { cache: "no-store" });
    return res.json();
  }

  // Fade the video in over its poster image once it's actually playing
  // (and hide the play icon at the same time); fade back to the static
  // image on pause/reset so there's never a moment showing neither. Shared
  // by both the Stories circles and Carousel cards — same DOM shape
  // (media wrapper > video + optional icon), different class names only.
  function attachFadeOnPlay(items, iconSelector) {
    items.forEach((el) => {
      const video = el.querySelector("video");
      const icon = iconSelector ? el.querySelector(iconSelector) : null;
      if (!video) return;
      video.addEventListener("playing", () => {
        video.style.opacity = "1";
        if (icon) icon.style.opacity = "0";
      });
      video.addEventListener("pause", () => {
        video.style.opacity = "0";
        if (icon) icon.style.opacity = "1";
      });
      video.addEventListener("emptied", () => {
        video.style.opacity = "0";
        if (icon) icon.style.opacity = "1";
      });
    });
  }

  // Two observers, deliberately not one: the video should start loading
  // well *before* it's actually on screen (a 600px rootMargin gives it a
  // head start, otherwise the live preview is still buffering by the time
  // the user scrolls to it), but it should only actually decode/play once
  // genuinely visible — no point running several off-screen previews.
  // Shared by Stories and Carousel (both are "a row of items that preview
  // themselves"); Floating Reels doesn't need this since its one bubble is
  // always on screen.
  function attachAutoplayRow(items, apiBase, videos, placement) {
    const preloadIO = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          const video = entry.target.querySelector("video");
          if (!video || !video.dataset.src) return;
          video.src = video.dataset.src;
          delete video.dataset.src;
        });
      },
      { rootMargin: "600px 0px", threshold: 0.01 }
    );
    // Impressions count once an item is at least half on screen — not when
    // it starts preloading 600px early (above), which used to count items
    // further down the page that the shopper never actually scrolled to.
    const seen = new WeakSet();
    const playIO = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const video = entry.target.querySelector("video");
          if (entry.isIntersecting && !seen.has(entry.target)) {
            seen.add(entry.target);
            sendEvent(apiBase, videos[Number(entry.target.dataset.index)].id, "impression", placement);
          }
          if (!video) return;
          if (entry.isIntersecting) video.play().catch(() => {});
          else video.pause();
        });
      },
      { threshold: 0.5 }
    );
    items.forEach((el) => {
      preloadIO.observe(el);
      playIO.observe(el);
    });
  }

  // ---------- Style: Stories (circle row + full-screen swipeable player) ----------
  function initStories(root, videos, apiBase, placement, desktopCircleSize) {
    const track = root.querySelector("[data-video-reels-track]");
    if (!track) return;

    // Inline styles throughout this file are deliberate: this widget is
    // embedded inside third-party theme markup that may have its own global
    // `button { ... }` resets with higher CSS specificity than a stylesheet
    // class selector, so critical layout/position properties are set here
    // directly to guarantee correct rendering regardless of host page CSS.
    const circleBtnStyle =
      "all:unset;box-sizing:border-box;flex:0 0 auto;width:80px;cursor:pointer;text-align:center;display:inline-block;";
    // Double ring, matching ReelTok: a thin white gap between the media and
    // the outer border, not the media touching the border directly. Border
    // color comes from --lr-accent (per-instance, set in initReelsWidget)
    // instead of a hardcoded value, so it's configurable in the theme editor.
    const circleMediaStyle =
      "display:block;box-sizing:border-box;width:80px;height:80px;padding:3px;border-radius:50%;border:3px solid var(--lr-accent, #cea761);background:#fff;position:relative;";
    const circleMediaInnerStyle = "display:block;width:100%;height:100%;border-radius:50%;overflow:hidden;background:#eee;position:relative;";
    const circleImgStyle = "display:block;width:100%;height:100%;object-fit:cover;background:#eee;";
    const circleVideoStyle =
      "display:block;width:100%;height:100%;object-fit:cover;position:absolute;inset:0;opacity:0;transition:opacity 0.2s ease;";
    const circlePlayIconStyle =
      "position:absolute;inset:0;display:flex;align-items:center;justify-content:center;color:#fff;text-shadow:0 1px 3px rgba(0,0,0,0.5);pointer-events:none;transition:opacity 0.15s ease;";

    // A plain <img> is the base layer, not a <video poster> — real-device
    // testing showed <video poster> paints inconsistently across browsers
    // (sometimes instant, sometimes never, with no clear pattern tied to
    // connection speed), which is exactly the "sometimes blank" symptom.
    // A plain image has none of those quirks: it always renders once
    // fetched, same as any other <img> on the page. The video is a
    // separate element layered on top, invisible until it's actually
    // playing, at which point it fades in over the image.
    track.innerHTML = videos
      .map(
        (v, i) => `
      <button class="video-reels__circle" style="${circleBtnStyle}" data-index="${i}" aria-label="Play video: ${escapeHtml(v.title)}">
        <span class="video-reels__circle-media" style="${circleMediaStyle}">
          <span class="video-reels__circle-media-inner" style="${circleMediaInnerStyle}">
            ${v.thumbnailUrl ? `<img src="${optimizedThumbnail(v.thumbnailUrl, 220)}" alt="" loading="lazy" style="${circleImgStyle}">` : ""}
            <video
              style="${circleVideoStyle}"
              data-src="${v.videoUrl}"
              muted
              loop
              playsinline
              preload="none"
            ></video>
            <span class="video-reels__circle-play-icon" style="${circlePlayIconStyle}">${ICON_PLAY_CIRCLE}</span>
          </span>
        </span>
      </button>
    `
      )
      .join("");

    const circles = track.querySelectorAll(".video-reels__circle");
    attachFadeOnPlay(circles, ".video-reels__circle-play-icon");

    // Desktop only: size the track so exactly as many circles as fit the
    // actual column width are shown in full, with zero partial circle
    // peeking off the edge. That fixed-width block is then centered via
    // CSS (`.video-reels__track { margin: 0 auto }`) — verified against
    // ReelTok's live widget, which centers its row rather than pinning it
    // to the column's left edge.
    //
    // The box used for centering is deliberately one gap wider than the
    // actual circle content (an invisible sliver of extra scroll space
    // after the last circle, not a visible one): ReelTok's own layout
    // includes a trailing margin on its last item in the width it centers
    // against, which shifts its visible row slightly left of true center
    // rather than dead-center. Matching that exactly (instead of a
    // dead-center row) is what lines up with the reference.
    const CIRCLE_GAP = 14;
    const DESKTOP_CIRCLE = desktopCircleSize || 100;
    function fitDesktopTrack() {
      if (!window.matchMedia("(min-width: 750px)").matches) {
        track.style.width = "";
        return;
      }
      const available = root.clientWidth;
      if (!available) return;
      let count = Math.floor((available + CIRCLE_GAP) / (DESKTOP_CIRCLE + CIRCLE_GAP));
      count = Math.max(1, Math.min(count, videos.length));
      const trackWidth = count * DESKTOP_CIRCLE + count * CIRCLE_GAP;
      track.style.width = trackWidth + "px";
    }
    fitDesktopTrack();
    if (window.ResizeObserver) {
      new ResizeObserver(fitDesktopTrack).observe(root);
    } else {
      window.addEventListener("resize", fitDesktopTrack);
    }

    attachAutoplayRow(circles, apiBase, videos, placement);

    track.addEventListener("click", (e) => {
      const btn = e.target.closest(".video-reels__circle");
      if (!btn) return;
      openOverlay(root, videos, Number(btn.dataset.index), apiBase, placement);
    });
  }

  // ---------- Style: Carousel (swipeable row of portrait cards) ----------
  function initCarousel(root, videos, apiBase, placement) {
    const track = root.querySelector("[data-video-reels-track]");
    if (!track) return;

    const cardBtnStyle = "all:unset;box-sizing:border-box;flex:0 0 auto;width:var(--lr-card-width, 140px);cursor:pointer;";
    const cardMediaStyle =
      "display:block;position:relative;width:100%;aspect-ratio:9/16;border-radius:12px;overflow:hidden;border:2px solid var(--lr-accent, #cea761);background:#eee;";
    const cardImgStyle = "position:absolute;inset:0;width:100%;height:100%;object-fit:cover;display:block;background:#eee;";
    const cardVideoStyle = "position:absolute;inset:0;width:100%;height:100%;object-fit:cover;display:block;opacity:0;transition:opacity 0.2s ease;";
    const cardCaptionStyle =
      "position:absolute;left:8px;right:8px;bottom:8px;color:#fff;font-size:12px;font-weight:600;line-height:1.3;text-shadow:0 1px 3px rgba(0,0,0,0.6);display:-webkit-box;-webkit-line-clamp:2;-webkit-box-orient:vertical;overflow:hidden;pointer-events:none;";
    const cardPlayIconStyle =
      "position:absolute;inset:0;display:flex;align-items:center;justify-content:center;color:#fff;text-shadow:0 1px 3px rgba(0,0,0,0.5);pointer-events:none;transition:opacity 0.15s ease;";

    track.innerHTML = videos
      .map(
        (v, i) => `
      <button class="video-reels__carousel-card" style="${cardBtnStyle}" data-index="${i}" aria-label="Play video: ${escapeHtml(v.title)}">
        <span class="video-reels__carousel-media" style="${cardMediaStyle}">
          ${v.thumbnailUrl ? `<img src="${optimizedThumbnail(v.thumbnailUrl, 400)}" alt="" loading="lazy" style="${cardImgStyle}">` : ""}
          <video
            style="${cardVideoStyle}"
            data-src="${v.videoUrl}"
            muted
            loop
            playsinline
            preload="none"
          ></video>
          <span class="video-reels__carousel-play-icon" style="${cardPlayIconStyle}">${ICON_PLAY_CIRCLE}</span>
          <span style="${cardCaptionStyle}">${escapeHtml(v.title)}</span>
        </span>
      </button>
    `
      )
      .join("");

    const cards = track.querySelectorAll(".video-reels__carousel-card");
    attachFadeOnPlay(cards, ".video-reels__carousel-play-icon");
    attachAutoplayRow(cards, apiBase, videos, placement);

    track.addEventListener("click", (e) => {
      const btn = e.target.closest(".video-reels__carousel-card");
      if (!btn) return;
      openOverlay(root, videos, Number(btn.dataset.index), apiBase, placement);
    });
  }

  // ---------- Style: Floating Reels (single sticky bubble, any corner) ----------
  function initFloating(root, videos, apiBase, placement) {
    const position = root.dataset.resolvedFloatingPosition || "bottom-right";
    const v = videos[0];

    const wrapStyle = "all:unset;box-sizing:border-box;position:fixed;z-index:2147483000;width:150px;";
    const cardStyle =
      "all:unset;box-sizing:border-box;display:block;position:relative;width:100%;aspect-ratio:9/16;border-radius:14px;overflow:hidden;cursor:pointer;border:2px solid var(--lr-accent, #cea761);box-shadow:0 8px 24px rgba(0,0,0,0.35);background:#000;";
    const imgStyle = "position:absolute;inset:0;width:100%;height:100%;object-fit:cover;display:block;";
    const videoStyle = "position:absolute;inset:0;width:100%;height:100%;object-fit:cover;display:block;opacity:0;transition:opacity 0.2s ease;";
    const captionStyle =
      "position:absolute;left:10px;right:10px;bottom:10px;color:#fff;font-size:13px;font-weight:600;line-height:1.3;text-shadow:0 1px 3px rgba(0,0,0,0.6);pointer-events:none;";
    const closeStyle =
      "all:unset;box-sizing:border-box;position:absolute;top:-10px;right:-10px;z-index:1;width:26px;height:26px;border-radius:50%;background:rgba(0,0,0,0.75);color:#fff;display:flex;align-items:center;justify-content:center;cursor:pointer;";

    const wrap = document.createElement("div");
    wrap.className = `video-reels__floating video-reels__floating--${position}`;
    wrap.style.cssText = wrapStyle + positionStyleFor(position);
    wrap.innerHTML = `
      <button class="video-reels__floating-close" style="${closeStyle}" aria-label="Close">${ICON_CLOSE}</button>
      <button class="video-reels__floating-card" style="${cardStyle}" aria-label="Play video: ${escapeHtml(v.title)}">
        ${v.thumbnailUrl ? `<img src="${optimizedThumbnail(v.thumbnailUrl, 400)}" alt="" style="${imgStyle}">` : ""}
        <video style="${videoStyle}" muted loop playsinline autoplay src="${v.videoUrl}"></video>
        <span style="${captionStyle}">${escapeHtml(v.title)}</span>
      </button>
    `;
    document.body.appendChild(wrap);

    const video = wrap.querySelector("video");
    video.addEventListener("playing", () => {
      video.style.opacity = "1";
    });
    video.play().catch(() => {});
    sendEvent(apiBase, v.id, "impression", placement);

    wrap.querySelector(".video-reels__floating-card").addEventListener("click", () => {
      openOverlay(root, videos, 0, apiBase, placement);
    });
    wrap.querySelector(".video-reels__floating-close").addEventListener("click", () => {
      video.pause();
      wrap.remove();
    });
  }

  // Shopify's Video sources are streaming-optimized re-encodes (e.g.
  // ".../c/vp/{hash}/{hash}.HD-1080p-7.2Mbps.mp4") — fine for the small
  // circle/card previews, but visibly softer than necessary once a video
  // fills the full-screen player. The untouched original upload sits at a
  // sibling URL sharing the same hash (".../c/o/v/{hash}.mp4"), confirmed
  // directly against Shopify's Admin API. This derives that URL purely
  // from the string — no extra request, no database field, no Shopify API
  // call at runtime — and is only ever tried for the full-screen player;
  // every other view keeps using videoUrl exactly as before. If a given
  // video doesn't match this pattern (not Shopify-hosted, or an unusual
  // upload format the original isn't safely swappable for), this simply
  // returns null and the player uses the normal URL, unchanged.
  function deriveHqSource(url) {
    if (!url) return null;
    const m = /^(https:\/\/cdn\.shopify\.com\/videos\/c\/)vp\/([a-z0-9]+)\/\2\.[^/?#]+\.mp4(?:[?#].*)?$/i.exec(url);
    return m ? `${m[1]}o/v/${m[2]}.mp4` : null;
  }

  function positionStyleFor(position) {
    switch (position) {
      case "bottom-left":
        return "bottom:16px;left:16px;";
      case "top-right":
        return "top:16px;right:16px;";
      case "top-left":
        return "top:16px;left:16px;";
      default:
        return "bottom:16px;right:16px;";
    }
  }

  // ---------- Style: Hero Banner (one full-width landscape video, above-the-fold) ----------
  function initHero(root, videos, apiBase, placement) {
    const track = root.querySelector("[data-video-reels-track]");
    if (!track) return;
    track.style.cssText = "display:block;overflow:visible;";

    const v = videos[0];
    const cardStyle =
      "all:unset;box-sizing:border-box;display:block;position:relative;width:100%;aspect-ratio:16/9;border-radius:10px;overflow:hidden;cursor:pointer;border:2px solid var(--lr-accent, #cea761);background:#000;";
    const imgStyle = "position:absolute;inset:0;width:100%;height:100%;object-fit:cover;display:block;";
    const videoStyle = "position:absolute;inset:0;width:100%;height:100%;object-fit:cover;display:block;opacity:0;transition:opacity 0.2s ease;";
    const playIconStyle =
      "position:absolute;inset:0;display:flex;align-items:center;justify-content:center;color:#fff;text-shadow:0 1px 4px rgba(0,0,0,0.6);pointer-events:none;transition:opacity 0.15s ease;";
    const captionStyle =
      "position:absolute;left:14px;right:14px;bottom:12px;color:#fff;font-size:14px;font-weight:600;line-height:1.3;text-shadow:0 1px 3px rgba(0,0,0,0.6);pointer-events:none;";

    track.innerHTML = `
      <button class="video-reels__hero-card" style="${cardStyle}" aria-label="Play video: ${escapeHtml(v.title)}">
        ${v.thumbnailUrl ? `<img src="${optimizedThumbnail(v.thumbnailUrl, 900)}" alt="" style="${imgStyle}">` : ""}
        <video style="${videoStyle}" data-src="${v.videoUrl}" muted loop playsinline preload="none"></video>
        <span class="video-reels__hero-play-icon" style="${playIconStyle}">${ICON_PLAY_CIRCLE_LG}</span>
        <span style="${captionStyle}">${escapeHtml(v.title)}</span>
      </button>
    `;

    const card = track.querySelector(".video-reels__hero-card");
    attachFadeOnPlay([card], ".video-reels__hero-play-icon");
    attachAutoplayRow([card], apiBase, videos, placement);
    card.addEventListener("click", () => openOverlay(root, videos, 0, apiBase, placement));
  }

  // ---------- Style: Grid Wall (responsive wrapping grid, below-the-fold gallery) ----------
  function initGrid(root, videos, apiBase, placement) {
    const track = root.querySelector("[data-video-reels-track]");
    if (!track) return;
    track.style.cssText = "display:grid;grid-template-columns:repeat(auto-fill, minmax(var(--lr-card-width, 140px), 1fr));gap:12px;overflow:visible;";

    const cardStyle =
      "all:unset;box-sizing:border-box;display:block;cursor:pointer;width:100%;";
    const mediaStyle =
      "display:block;position:relative;width:100%;aspect-ratio:9/16;border-radius:10px;overflow:hidden;border:2px solid var(--lr-accent, #cea761);background:#eee;";
    const imgStyle = "position:absolute;inset:0;width:100%;height:100%;object-fit:cover;display:block;background:#eee;";
    const videoStyle = "position:absolute;inset:0;width:100%;height:100%;object-fit:cover;display:block;opacity:0;transition:opacity 0.2s ease;";
    const playIconStyle =
      "position:absolute;inset:0;display:flex;align-items:center;justify-content:center;color:#fff;text-shadow:0 1px 3px rgba(0,0,0,0.5);pointer-events:none;transition:opacity 0.15s ease;";
    const captionStyle =
      "position:absolute;left:8px;right:8px;bottom:8px;color:#fff;font-size:12px;font-weight:600;line-height:1.3;text-shadow:0 1px 3px rgba(0,0,0,0.6);display:-webkit-box;-webkit-line-clamp:2;-webkit-box-orient:vertical;overflow:hidden;pointer-events:none;";

    track.innerHTML = videos
      .map(
        (v, i) => `
      <button class="video-reels__grid-card" style="${cardStyle}" data-index="${i}" aria-label="Play video: ${escapeHtml(v.title)}">
        <span class="video-reels__grid-media" style="${mediaStyle}">
          ${v.thumbnailUrl ? `<img src="${optimizedThumbnail(v.thumbnailUrl, 400)}" alt="" loading="lazy" style="${imgStyle}">` : ""}
          <video style="${videoStyle}" data-src="${v.videoUrl}" muted loop playsinline preload="none"></video>
          <span class="video-reels__grid-play-icon" style="${playIconStyle}">${ICON_PLAY_CIRCLE}</span>
          <span style="${captionStyle}">${escapeHtml(v.title)}</span>
        </span>
      </button>
    `
      )
      .join("");

    const cards = track.querySelectorAll(".video-reels__grid-card");
    attachFadeOnPlay(cards, ".video-reels__grid-play-icon");
    attachAutoplayRow(cards, apiBase, videos, placement);
    track.addEventListener("click", (e) => {
      const btn = e.target.closest(".video-reels__grid-card");
      if (!btn) return;
      openOverlay(root, videos, Number(btn.dataset.index), apiBase, placement);
    });
  }

  // ---------- Style: Filmstrip (compact row of small square thumbnails) ----------
  function initFilmstrip(root, videos, apiBase, placement, thumbSize) {
    const track = root.querySelector("[data-video-reels-track]");
    if (!track) return;

    const size = thumbSize || 64;
    const btnStyle = `all:unset;box-sizing:border-box;flex:0 0 auto;width:${size}px;cursor:pointer;`;
    const mediaStyle = `display:block;position:relative;width:${size}px;height:${size}px;border-radius:6px;overflow:hidden;border:1.5px solid var(--lr-accent, #cea761);background:#eee;`;
    const imgStyle = "position:absolute;inset:0;width:100%;height:100%;object-fit:cover;display:block;background:#eee;";
    const videoStyle = "position:absolute;inset:0;width:100%;height:100%;object-fit:cover;display:block;opacity:0;transition:opacity 0.2s ease;";
    const playIconStyle =
      "position:absolute;inset:0;display:flex;align-items:center;justify-content:center;color:#fff;text-shadow:0 1px 2px rgba(0,0,0,0.5);pointer-events:none;transition:opacity 0.15s ease;";

    track.innerHTML = videos
      .map(
        (v, i) => `
      <button class="video-reels__filmstrip-item" style="${btnStyle}" data-index="${i}" aria-label="Play video: ${escapeHtml(v.title)}">
        <span class="video-reels__filmstrip-media" style="${mediaStyle}">
          ${v.thumbnailUrl ? `<img src="${optimizedThumbnail(v.thumbnailUrl, 160)}" alt="" loading="lazy" style="${imgStyle}">` : ""}
          <video style="${videoStyle}" data-src="${v.videoUrl}" muted loop playsinline preload="none"></video>
          <span class="video-reels__filmstrip-play-icon" style="${playIconStyle}">${ICON_PLAY_CIRCLE_SM}</span>
        </span>
      </button>
    `
      )
      .join("");

    const items = track.querySelectorAll(".video-reels__filmstrip-item");
    attachFadeOnPlay(items, ".video-reels__filmstrip-play-icon");
    attachAutoplayRow(items, apiBase, videos, placement);
    track.addEventListener("click", (e) => {
      const btn = e.target.closest(".video-reels__filmstrip-item");
      if (!btn) return;
      openOverlay(root, videos, Number(btn.dataset.index), apiBase, placement);
    });
  }

  // ---------- Style: Spotlight Card (one big portrait "ad" card, inline) ----------
  function initSpotlight(root, videos, apiBase, placement) {
    const track = root.querySelector("[data-video-reels-track]");
    if (!track) return;
    track.style.cssText = "display:block;overflow:visible;max-width:280px;";

    const v = videos[0];
    const cardStyle =
      "all:unset;box-sizing:border-box;display:block;position:relative;width:100%;aspect-ratio:9/16;border-radius:14px;overflow:hidden;cursor:pointer;border:2px solid var(--lr-accent, #cea761);box-shadow:0 6px 18px rgba(0,0,0,0.18);background:#000;";
    const imgStyle = "position:absolute;inset:0;width:100%;height:100%;object-fit:cover;display:block;";
    const videoStyle = "position:absolute;inset:0;width:100%;height:100%;object-fit:cover;display:block;opacity:0;transition:opacity 0.2s ease;";
    const playIconStyle =
      "position:absolute;inset:0;display:flex;align-items:center;justify-content:center;color:#fff;text-shadow:0 1px 4px rgba(0,0,0,0.6);pointer-events:none;transition:opacity 0.15s ease;";
    const captionStyle =
      "position:absolute;left:14px;right:14px;bottom:14px;color:#fff;font-size:14px;font-weight:600;line-height:1.3;text-shadow:0 1px 3px rgba(0,0,0,0.6);pointer-events:none;";
    const ctaStyle =
      "position:absolute;top:12px;left:12px;background:rgba(0,0,0,0.55);color:#fff;font-size:11px;font-weight:700;letter-spacing:0.03em;text-transform:uppercase;padding:4px 9px;border-radius:99px;pointer-events:none;";

    track.innerHTML = `
      <button class="video-reels__spotlight-card" style="${cardStyle}" aria-label="Play video: ${escapeHtml(v.title)}">
        ${v.thumbnailUrl ? `<img src="${optimizedThumbnail(v.thumbnailUrl, 600)}" alt="" style="${imgStyle}">` : ""}
        <video style="${videoStyle}" data-src="${v.videoUrl}" muted loop playsinline preload="none"></video>
        <span style="${ctaStyle}">Watch</span>
        <span class="video-reels__spotlight-play-icon" style="${playIconStyle}">${ICON_PLAY_CIRCLE_LG}</span>
        <span style="${captionStyle}">${escapeHtml(v.title)}</span>
      </button>
    `;

    const card = track.querySelector(".video-reels__spotlight-card");
    attachFadeOnPlay([card], ".video-reels__spotlight-play-icon");
    attachAutoplayRow([card], apiBase, videos, placement);
    card.addEventListener("click", () => openOverlay(root, videos, 0, apiBase, placement));
  }

  // ---------- Full-screen swipeable player (shared by all styles) ----------
  const overlayStyle =
    "all:unset;box-sizing:border-box;position:fixed;inset:0;z-index:2147483000;background:rgba(0,0,0,0.92);align-items:center;justify-content:center;display:none;";
  const overlayOpenStyle = overlayStyle.replace("display:none", "display:flex");
  // Close/prev/next default to position:absolute here — real positioning
  // (top/right/transform, and the desktop position:fixed override) is
  // entirely CSS-driven (external stylesheet, !important) so it can
  // respond to the breakpoint media query.
  const closeBtnStyle =
    "all:unset;box-sizing:border-box;width:44px;height:44px;border-radius:50%;background:rgba(255,255,255,0.15);color:#fff;display:flex;align-items:center;justify-content:center;font-size:20px;line-height:1;cursor:pointer;";
  const navBtnStyle =
    "all:unset;box-sizing:border-box;width:40px;height:40px;border-radius:50%;background:rgba(255,255,255,0.15);color:#fff;display:flex;align-items:center;justify-content:center;font-size:20px;line-height:1;cursor:pointer;";
  // The stage is the real card-sized box (a stable DOM element close/prev/next
  // anchor to via position:absolute) — sized once here; the scrollable card
  // itself just fills it with inset:0 (set in CSS), so nothing needs
  // viewport vh/vw math to line up with it.
  const stageStyle = "box-sizing:border-box;position:relative;width:min(90vw, 420px);height:min(calc(90vw * 16 / 9), 88vh);max-height:88vh;";
  // The scrollable feed itself: one slide per video, native vertical
  // scroll-snap so the browser drives momentum/rubber-banding exactly like
  // TikTok/Instagram's own feeds — no custom drag math trying to fake it.
  const viewportStyle =
    "box-sizing:border-box;overflow-y:auto;overflow-x:hidden;background:#000;scroll-snap-type:y mandatory;scrollbar-width:none;-webkit-overflow-scrolling:touch;";
  const slideStyle =
    "box-sizing:border-box;position:relative;width:100%;height:100%;scroll-snap-align:start;scroll-snap-stop:always;flex-shrink:0;";
  // Behind the video, not a <video poster> — same reliability reasoning as
  // the circle row. background:#000 stays on the slide/card itself so
  // there's still a letterbox backdrop once the video is actually playing
  // (object-fit:contain), but the image is what's visible while it loads.
  const slideImgStyle = "position:absolute;inset:0;width:100%;height:100%;object-fit:cover;display:block;";
  const videoStyle2 =
    "position:absolute;inset:0;display:block;width:100%;height:100%;object-fit:contain;background:transparent;";
  const muteBtnStyle =
    "all:unset;box-sizing:border-box;position:absolute;top:56px;right:12px;z-index:2;width:36px;height:36px;border-radius:50%;background:rgba(0,0,0,0.5);color:#fff;display:flex;align-items:center;justify-content:center;font-size:16px;cursor:pointer;";
  const pauseBtnStyle =
    "all:unset;box-sizing:border-box;position:absolute;top:12px;right:12px;z-index:2;width:36px;height:36px;border-radius:50%;background:rgba(0,0,0,0.5);color:#fff;display:flex;align-items:center;justify-content:center;font-size:16px;cursor:pointer;";
  // Playback progress bar, top of each slide — matches Stories/YouTube-style indicators.
  const progressTrackStyle =
    "box-sizing:border-box;position:absolute;top:0;left:0;right:0;z-index:2;height:3px;background:rgba(255,255,255,0.3);";
  const progressFillStyle = "display:block;height:100%;width:0%;background:#fff;transition:width 0.1s linear;";

  // Plain line-style SVG icons instead of emoji, which render inconsistently
  // (and sometimes garishly) across OS/browser combinations.
  const ICON_CLOSE =
    '<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"><path d="M6 6l12 12M18 6L6 18"/></svg>';
  const ICON_CHEVRON_LEFT =
    '<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M15 5l-7 7 7 7"/></svg>';
  const ICON_CHEVRON_RIGHT =
    '<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M9 5l7 7-7 7"/></svg>';
  const ICON_PLAY = '<svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M8 5v14l11-7z"/></svg>';
  const ICON_PAUSE =
    '<svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M6 5h4v14H6zM14 5h4v14h-4z"/></svg>';
  const ICON_MUTE =
    '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M3 9v6h4l5 5V4L7 9H3z" fill="currentColor" stroke="none"/><path d="M16 9l6 6M22 9l-6 6"/></svg>';
  const ICON_UNMUTE =
    '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M3 9v6h4l5 5V4L7 9H3z" fill="currentColor" stroke="none"/><path d="M16 8a5 5 0 010 8M19 5a9 9 0 010 14"/></svg>';
  const ICON_PLAY_CIRCLE =
    '<svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><circle cx="12" cy="12" r="10"/><path d="M10 8.5v7l6-3.5z" fill="currentColor" stroke="none"/></svg>';
  const ICON_PLAY_CIRCLE_SM =
    '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><circle cx="12" cy="12" r="10"/><path d="M10 8.5v7l6-3.5z" fill="currentColor" stroke="none"/></svg>';
  const ICON_PLAY_CIRCLE_LG =
    '<svg width="46" height="46" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.2"><circle cx="12" cy="12" r="10"/><path d="M10 8.5v7l6-3.5z" fill="currentColor" stroke="none"/></svg>';

  function openOverlay(root, videos, startIndex, apiBase, placement) {
    let state = rootOverlays.get(root);

    if (!state) {
      const overlay = document.createElement("div");
      overlay.className = "video-reels__overlay";
      overlay.style.cssText = overlayStyle;
      overlay.innerHTML = `
        <div class="video-reels__stage" style="${stageStyle}">
          <button class="video-reels__overlay-close" style="${closeBtnStyle}" aria-label="Close">${ICON_CLOSE}</button>
          <button class="video-reels__overlay-prev" style="${navBtnStyle}" aria-label="Previous video">${ICON_CHEVRON_LEFT}</button>
          <button class="video-reels__overlay-next" style="${navBtnStyle}" aria-label="Next video">${ICON_CHEVRON_RIGHT}</button>
          <div class="video-reels__card" style="${viewportStyle}"></div>
        </div>
      `;
      // Appended directly to <body> — never nested inside the widget's own
      // position in the page. Any ancestor with a CSS transform (this
      // theme's sticky product-info wrapper included) turns itself into the
      // containing block for descendant `position: fixed` elements, which
      // breaks true full-viewport overlays. Attaching to body sidesteps that
      // entirely.
      document.body.appendChild(overlay);

      const viewport = overlay.querySelector(".video-reels__card");
      const muteBtn = overlay.querySelector(".video-reels__mute");
      const prevBtn = overlay.querySelector(".video-reels__overlay-prev");
      const nextBtn = overlay.querySelector(".video-reels__overlay-next");

      state = {
        overlay,
        viewport,
        prevBtn,
        nextBtn,
        videos,
        apiBase,
        placement,
        index: 0,
        // Separate from `index`: `index` drives nav-button visibility and
        // must be correct synchronously (before the observer has fired
        // even once), so it's set eagerly to startIndex on every open.
        // openTrackedIndex is only the analytics dedup guard — reset to -1
        // on every open so the "open" event for whichever slide is
        // actually shown first still fires (see openOverlay below).
        openTrackedIndex: -1,
        muted: true,
        slides: [],
        suppressScrollEvents: false,
      };
      rootOverlays.set(root, state);

      function buildSlides() {
        state.slides.forEach((s) => {
          const tracker = trackerByVideo.get(s.video);
          if (tracker) tracker.end();
        });
        viewport.innerHTML = state.videos
          .map(
            (v, i) => `
          <div class="video-reels__slide" data-index="${i}" style="${slideStyle}">
            <div class="video-reels__progress-track" style="${progressTrackStyle}">
              <div class="video-reels__progress-fill" style="${progressFillStyle}"></div>
            </div>
            ${v.thumbnailUrl ? `<img src="${optimizedThumbnail(v.thumbnailUrl, 800)}" alt="" style="${slideImgStyle}">` : ""}
            <video style="${videoStyle2}" playsinline loop data-src="${deriveHqSource(v.videoUrl) || v.videoUrl}" data-fallback-src="${v.videoUrl}"></video>
            <button class="video-reels__pause" style="${pauseBtnStyle}" aria-label="Pause">${ICON_PAUSE}</button>
            <button class="video-reels__mute" style="${muteBtnStyle}" aria-label="Unmute">${ICON_MUTE}</button>
          </div>
        `
          )
          .join("");

        state.slides = Array.from(viewport.querySelectorAll(".video-reels__slide")).map((el) => {
          const video = el.querySelector("video");
          const pauseBtn = el.querySelector(".video-reels__pause");
          const muteBtn = el.querySelector(".video-reels__mute");
          const progressFill = el.querySelector(".video-reels__progress-fill");

          video.addEventListener("timeupdate", () => {
            if (!video.duration) return;
            progressFill.style.width = `${(video.currentTime / video.duration) * 100}%`;
          });
          function togglePlay() {
            if (video.paused) video.play().catch(() => {});
            else video.pause();
          }
          video.addEventListener("click", togglePlay);
          pauseBtn.addEventListener("click", togglePlay);
          video.addEventListener("play", () => {
            pauseBtn.innerHTML = ICON_PAUSE;
            pauseBtn.setAttribute("aria-label", "Pause");
          });
          // Once it has an actual frame to show, switch to an opaque black
          // background so the letterbox bars (object-fit:contain) are clean
          // black rather than showing the backdrop image through them.
          video.addEventListener("playing", () => {
            video.style.background = "#000";
          });
          video.addEventListener("pause", () => {
            pauseBtn.innerHTML = ICON_PLAY;
            pauseBtn.setAttribute("aria-label", "Play");
          });
          video.addEventListener("emptied", () => {
            video.style.background = "transparent";
          });
          // Falls back to the known-good streaming URL if the derived
          // original-quality URL 404s (e.g. an upload format the original
          // isn't safely swappable for). Clears fallbackSrc before
          // retrying so a second error — the fallback itself failing,
          // which would mean the video is broken outright — doesn't loop.
          video.addEventListener("error", () => {
            const fallback = video.dataset.fallbackSrc;
            if (!fallback || video.src === fallback) return;
            video.dataset.fallbackSrc = "";
            video.src = fallback;
            video.load();
            video.play().catch(() => {});
          });
          muteBtn.addEventListener("click", () => {
            state.muted = !state.muted;
            state.slides.forEach((s) => (s.video.muted = state.muted));
            state.slides.forEach((s) => {
              s.muteBtn.innerHTML = state.muted ? ICON_MUTE : ICON_UNMUTE;
              s.muteBtn.setAttribute("aria-label", state.muted ? "Unmute" : "Mute");
            });
          });

          return { el, video, pauseBtn, muteBtn, progressFill };
        });

        // Preload the adjacent slide's video before it's actually swiped
        // to — a 100% rootMargin (relative to the viewport's own size)
        // covers roughly one full slide ahead/behind, so by the time a
        // swipe completes the next clip already has a head start instead
        // of starting the fetch only once fully in view.
        state.slidePreloadObserver = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              if (!entry.isIntersecting) return;
              const idx = Number(entry.target.dataset.index);
              const slide = state.slides[idx];
              if (!slide || !slide.video.dataset.src) return;
              slide.video.src = slide.video.dataset.src;
              delete slide.video.dataset.src;
            });
          },
          { root: viewport, rootMargin: "100% 100%", threshold: 0.01 }
        );

        // Drives play/pause/current-index purely off which slide is
        // actually visible — the same mechanism whether the user got there
        // by native scroll, the arrow buttons, or keyboard, so there's a
        // single source of truth for "what's playing now."
        state.slideObserver = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              const idx = Number(entry.target.dataset.index);
              const slide = state.slides[idx];
              if (!slide) return;
              if (entry.isIntersecting && entry.intersectionRatio > 0.6) {
                if (state.openTrackedIndex !== idx) {
                  state.openTrackedIndex = idx;
                  sendEvent(state.apiBase, state.videos[idx].id, "open", state.placement);
                  startSession(state, idx);
                }
                if (state.index !== idx) {
                  state.index = idx;
                  updateNavVisibility();
                }
                if (slide.video.dataset.src) {
                  slide.video.src = slide.video.dataset.src;
                  delete slide.video.dataset.src;
                }
                slide.video.muted = state.muted;
                slide.video.play().catch(() => {});
              } else {
                notePauseReason(slide.video, "switched_video");
                slide.video.pause();
              }
            });
          },
          { root: viewport, threshold: [0, 0.6, 1] }
        );
        state.slides.forEach((s) => {
          state.slidePreloadObserver.observe(s.el);
          state.slideObserver.observe(s.el);
        });
      }
      state.buildSlides = buildSlides;

      function updateNavVisibility() {
        prevBtn.style.visibility = state.index === 0 ? "hidden" : "visible";
        nextBtn.style.visibility = state.index === state.videos.length - 1 ? "hidden" : "visible";
      }
      state.updateNavVisibility = updateNavVisibility;

      // "nearest" on both axes (rather than hard-coding "start") lets this
      // work whether the feed is currently scrolling vertically (mobile) or
      // horizontally (desktop) — only the axis that's actually scrollable
      // moves, and CSS scroll-snap still pulls it fully into place.
      function goTo(idx) {
        if (idx < 0 || idx >= state.slides.length) return;
        state.slides[idx].el.scrollIntoView({ behavior: "smooth", block: "nearest", inline: "nearest" });
      }

      overlay.querySelector(".video-reels__overlay-close").addEventListener("click", () => closeOverlay(root));
      prevBtn.addEventListener("click", () => goTo(state.index - 1));
      nextBtn.addEventListener("click", () => goTo(state.index + 1));
      // Click anywhere on the dark backdrop (outside the stage) to close —
      // e.target === overlay only when the click lands on the overlay
      // itself, not something bubbling up from inside the stage/card.
      overlay.addEventListener("click", (e) => {
        if (e.target === overlay) closeOverlay(root);
      });

      document.addEventListener("keydown", (e) => {
        if (overlay.style.display === "none" || !overlay.style.display) return;
        if (e.key === "Escape") closeOverlay(root);
        if (e.key === "ArrowUp" || e.key === "ArrowLeft") goTo(state.index - 1);
        if (e.key === "ArrowDown" || e.key === "ArrowRight") goTo(state.index + 1);
      });
    }

    state.videos = videos;
    state.index = startIndex;
    // Reset on every open (not just the first): openTrackedIndex is the
    // analytics dedup guard in the slideObserver below, which only fires
    // "open" when a slide's index differs from it. Without resetting this,
    // a video closed and reopened would never fire again either.
    //
    // The starting slide's "open" fires here, synchronously, rather than
    // waiting for slideObserver to notice it's >60% visible — relying on
    // IntersectionObserver for the very first slide was fragile in
    // practice (its callback only runs once the browser has actually
    // painted a frame after the overlay's display:none → flex flip, which
    // isn't guaranteed to happen promptly, or at all, before the user
    // swipes away or closes again). A direct click is already 100%
    // confirmation the video was opened; no need to wait on a visibility
    // computation to say so. slideObserver still covers "open" for videos
    // reached by swiping further within the overlay.
    state.openTrackedIndex = startIndex;
    sendEvent(apiBase, videos[startIndex].id, "open", placement);
    state.buildSlides();
    startSession(state, startIndex);
    state.updateNavVisibility();
    state.overlay.style.cssText = overlayOpenStyle;
    document.body.style.overflow = "hidden";
    // Jump to the tapped circle's video instantly (no scroll animation) —
    // the animation is reserved for in-feed navigation, not the open transition.
    if (state.slides[startIndex]) {
      state.slides[startIndex].el.scrollIntoView({ behavior: "auto", block: "nearest", inline: "nearest" });
    }
  }

  function closeOverlay(root) {
    const state = rootOverlays.get(root);
    if (!state) return;
    state.overlay.style.cssText = overlayStyle;
    document.body.style.overflow = "";
    state.slides.forEach((s) => {
      notePauseReason(s.video, "closed");
      s.video.pause();
    });
  }

  // One playback session per opening of a slide — swiping back to a video
  // starts a fresh session for it (see trackPlayback).
  function startSession(state, idx) {
    const slide = state.slides[idx];
    const meta = placementMeta.get(state.placement) || {};
    if (!slide || meta.analytics === false) return;
    trackPlayback(slide.video, {
      apiBase: state.apiBase,
      surface: "reels",
      store: meta.store || "uk",
      getVideoId: () => state.videos[idx] && state.videos[idx].id,
      getPlacementId: () => meta.placementId,
    });
  }

  function sendEvent(apiBase, videoId, type, placementHandle, value) {
    const meta = placementMeta.get(placementHandle) || {};
    if (meta.analytics === false) return;
    const base = { videoId, type, placementHandle, placementType: "reels", store: meta.store, placementId: meta.placementId || undefined };
    const payload = JSON.stringify(value === undefined ? base : { ...base, value });
    const url = `${apiBase}/api/analytics/event`;
    if (navigator.sendBeacon) {
      navigator.sendBeacon(url, new Blob([payload], { type: "application/json" }));
    } else {
      fetch(url, { method: "POST", headers: { "Content-Type": "application/json" }, body: payload, keepalive: true });
    }
  }

  function escapeHtml(str) {
    return String(str || "").replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]));
  }
})();